/**
 * AXIOM-WEB-2 Tier 1 — cross-tab refresh sync
 *
 * 다중 탭/PWA window 가 동일 cookie 로 동시에 `/auth/refresh` 를 fire 하지 못하도록
 * 두 layer 로 직렬화한다.
 *
 * 1) Web Lock API (navigator.locks) — 지원 환경 (Chrome 69+, Firefox 96+, Safari 16.4+,
 *    Edge 79+) 에서 진정한 cross-window mutex
 * 2) localStorage 기반 winner-takes-all claim mutex — Web Locks 미지원 환경의 fallback.
 *    claim 키에 uid 쓰고 짧게 대기 후 자기 uid 가 살아있으면 lock 획득, 아니면 양보.
 *    atomic mutex 는 아니지만 race window 를 50ms 로 좁혀 token-reuse 충돌 빈도 ↓
 *
 * 두 layer 모두 lock 획득 후 localStorage grace window (graceMs) 안에 이미 refresh 가
 * 일어났다면 자기 perform 을 skip — caller 는 새 cookie 로 retry 만 시도.
 *
 * BE 의 token-reuse-detection (auth.service.ts:151-158) 회피.
 */

const STORAGE_KEY = 'axiom-auth-last-refresh-at';
const CLAIM_KEY = 'axiom-auth-refresh-claim';
const DEFAULT_GRACE_MS = 3000;
const DEFAULT_CLAIM_WAIT_MS = 50;
const LOCK_WAIT_TIMEOUT_MS = 10_000;

export class RefreshLockTimeoutError extends Error {
  override readonly name = 'RefreshLockTimeoutError';

  constructor() {
    super('Timed out waiting for the session refresh lock');
  }
}

export interface LockManagerLike {
  request<T>(
    name: string,
    options: { mode: 'exclusive' | 'shared'; signal?: AbortSignal },
    cb: () => Promise<T>
  ): Promise<T>;
}

export interface RefreshLockOptions {
  now?: () => number;
  storage?: Storage | null;
  locks?: LockManagerLike | null;
  graceMs?: number;
  /** fallback claim mutex 가 winner 결정을 위해 대기하는 시간 */
  claimWaitMs?: number;
  /** fallback claim mutex 의 wait 구현체 (test injection) */
  sleep?: (ms: number) => Promise<void>;
  /** fallback claim mutex 의 uid 생성 (test injection) */
  uid?: () => string;
}

export interface RefreshLockResult<T> {
  result: T | null;
  usedGrace: boolean;
}

function resolveLocks(explicit: LockManagerLike | null | undefined): LockManagerLike | null {
  if (explicit !== undefined) return explicit;
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return (navigator as Navigator & { locks: LockManagerLike }).locks;
  }
  return null;
}

function resolveStorage(explicit: Storage | null | undefined): Storage | null {
  if (explicit !== undefined) return explicit;
  if (typeof localStorage !== 'undefined') return localStorage;
  return null;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function defaultUid(now: () => number): string {
  // crypto.randomUUID 가 없을 수 있어 시간 + Math.random 기반
  return `${now()}:${Math.random().toString(36).slice(2, 10)}`;
}

function checkGrace(storage: Storage, now: () => number, graceMs: number): boolean {
  const raw = storage.getItem(STORAGE_KEY);
  if (raw === null) return false;
  const lastAt = Number(raw);
  return Number.isFinite(lastAt) && lastAt > 0 && now() - lastAt < graceMs;
}

export async function withCrossTabRefreshLock<T>(
  perform: () => Promise<T>,
  options: RefreshLockOptions = {}
): Promise<RefreshLockResult<T>> {
  const now = options.now ?? Date.now;
  const storage = resolveStorage(options.storage);
  const locks = resolveLocks(options.locks);
  const graceMs = options.graceMs ?? DEFAULT_GRACE_MS;
  const claimWaitMs = options.claimWaitMs ?? DEFAULT_CLAIM_WAIT_MS;
  const sleep = options.sleep ?? defaultSleep;
  const uid = options.uid ?? (() => defaultUid(now));

  // navigator.locks 지원 — Web Lock API 가 진정한 cross-window mutex 보장
  if (locks) {
    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort(new RefreshLockTimeoutError()),
      LOCK_WAIT_TIMEOUT_MS
    );
    try {
      return await locks.request(
        'axiom-auth-refresh',
        { mode: 'exclusive', signal: controller.signal },
        async () => {
          // 획득 대기만 제한한다. 진행 중인 갱신 요청은 HTTP 타임아웃이 담당한다.
          clearTimeout(timer);
          if (storage && checkGrace(storage, now, graceMs)) {
            return { result: null, usedGrace: true };
          }
          const result = await perform();
          storage?.setItem(STORAGE_KEY, String(now()));
          return { result, usedGrace: false };
        }
      );
    } finally {
      clearTimeout(timer);
    }
  }

  // fallback — storage 도 없으면 lock 불가, 직접 perform
  if (!storage) {
    const result = await perform();
    return { result, usedGrace: false };
  }

  // fallback grace 1차 검사
  if (checkGrace(storage, now, graceMs)) {
    return { result: null, usedGrace: true };
  }

  // winner-takes-all claim. setItem 후 짧게 대기 → 자기 uid 가 살아있으면 lock 보유.
  // 두 탭이 거의 동시에 claim 하면 마지막 setItem 이 winner — last-write-wins.
  const myUid = uid();
  storage.setItem(CLAIM_KEY, myUid);
  await sleep(claimWaitMs);

  if (storage.getItem(CLAIM_KEY) !== myUid) {
    // 다른 탭이 덮어씀 → 양보. 다른 탭이 refresh 완료했을 가능성 있어 grace 재검사.
    // 미완료라도 caller 는 retry 만 시도하므로 새 cookie 로 통과 또는 다시 401 → 다음 cycle.
    return { result: null, usedGrace: true };
  }

  try {
    // claim 보유 — 마지막으로 grace 재검사 (claimWait 동안 다른 layer 가 갱신했을 수 있음)
    if (checkGrace(storage, now, graceMs)) {
      return { result: null, usedGrace: true };
    }
    const result = await perform();
    storage.setItem(STORAGE_KEY, String(now()));
    return { result, usedGrace: false };
  } finally {
    if (storage.getItem(CLAIM_KEY) === myUid) {
      storage.removeItem(CLAIM_KEY);
    }
  }
}
